import { lazy } from 'react';
import logger from './logger';

// Session-scoped guard so we only force one reload per tab. If the new bundle
// still can't load after a refresh we stop retrying and let the error bubble
// up to the ErrorBoundary instead of looping forever.
const RELOAD_KEY = 'gs:chunkReloaded';

// Webpack surfaces chunk-load failures through a few different names/messages
// depending on whether it's a JS chunk (ChunkLoadError) or a CSS chunk
// extracted by mini-css-extract-plugin (CSS_CHUNK_LOAD_FAILED).
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const code = error.code || '';
  const message = error.message || String(error);
  return (
    name === 'ChunkLoadError' ||
    code === 'CSS_CHUNK_LOAD_FAILED' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
  );
}

function hasAlreadyReloaded() {
  try {
    return window.sessionStorage.getItem(RELOAD_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, '1');
  } catch (_) {
    /* storage disabled — best-effort guard only */
  }
}

// After a successful app boot we know the current bundle is healthy, so clear
// the guard. Otherwise a single past reload would permanently disable retries
// for this tab across future deploys.
export function clearChunkReloadFlag() {
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch (_) {
    /* noop */
  }
}

// Wraps React.lazy so that a chunk-load failure (typically caused by the user
// having an old tab open across a deploy, where the old hashed chunk URL no
// longer exists on Hosting) triggers a single hard reload to pick up the new
// bundle instead of surfacing a scary error screen.
export default function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error) && !hasAlreadyReloaded()) {
        logger.warn(
          'Chunk load failed — likely stale bundle after deploy, reloading',
          error
        );
        markReloaded();
        window.location.reload();
        // Return a never-resolving promise so Suspense keeps showing the
        // fallback spinner until the reload replaces the page.
        return new Promise(() => {});
      }
      throw error;
    })
  );
}
